import { getByPath } from "./bindings";

interface TemplateColumn {
  key: string;
  label: string;
  format?: string;
  exportTier?: string;
}

interface TableInfo {
  sheetName: string;
  columns: TemplateColumn[];
  rows: any[];
}

interface ExportOptions {
  template: any;
  data: any;
  fileName: string;
  columnFilter?: (col: TemplateColumn) => boolean;
  isCurrent?: () => boolean;
}

/**
 * Walk template blocks to extract all exportable tables.
 * Handles top-level tables, repeat blocks with inner tables,
 * and nested sections. Each repeat item becomes a separate sheet.
 */
function extractTables(
  blocks: any[],
  data: any,
  parentName: string,
): TableInfo[] {
  const tables: TableInfo[] = [];

  for (const block of blocks) {
    if (block.type === "table") {
      const rowsPath = block.rows?.path;
      const rows = rowsPath ? getByPath(rowsPath, data) : [];
      const sheetName =
        typeof block.exportSheetName === "string" ? block.exportSheetName : parentName;
      tables.push({
        sheetName,
        columns: block.columns || [],
        rows: Array.isArray(rows) ? rows : [],
      });
    }

    if (block.type === "repeat" && block.each?.path) {
      const items = getByPath(block.each.path, data);
      if (Array.isArray(items)) {
        items.forEach((item: any, idx: number) => {
          const scoped = { ...data, ...item, $root: data, $index: idx };
          const label =
            item.payeeName || item.name || item.broker || item.landman || `Item ${idx + 1}`;
          const innerBlocks = block.blocks || [];
          const innerTables = extractTables(innerBlocks, scoped, label);
          tables.push(...innerTables);
        });
      }
    }

    // Walk into group/section blocks that may contain tables
    if (
      (block.type === "group" || block.type === "section") &&
      block.blocks
    ) {
      tables.push(...extractTables(block.blocks, data, parentName));
    }
  }

  return tables;
}

/** Collect all blocks from all sections in a template layout */
function getAllBlocks(template: any): any[] {
  const sections = template?.layout?.sections || [];
  const blocks: any[] = [];
  for (const section of sections) {
    if (section.blocks) blocks.push(...section.blocks);
  }
  return blocks;
}

/** Parse a date value into an Excel-compatible Date object */
function parseDate(v: any): Date | null {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

/** Sanitize sheet name: Excel limits to 31 chars, disallows certain characters */
function sanitizeSheetName(name: string): string {
  return name
    .replace(/[\\/*?[\]:]/g, "")
    .substring(0, 31)
    .trim() || "Sheet";
}

/** Ensure unique sheet names (Excel requires uniqueness) */
function deduplicateSheetNames(tables: TableInfo[]): void {
  const seen = new Map<string, number>();
  for (const t of tables) {
    const base = sanitizeSheetName(t.sheetName);
    const count = seen.get(base) || 0;
    seen.set(base, count + 1);
    t.sheetName = count === 0 ? base : sanitizeSheetName(`${base} (${count})`);
  }
}

/** Trigger a browser download of a Blob */
export function triggerDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Export report data to an Excel workbook and trigger download.
 * Lazy-loads the xlsx library to keep the main bundle small.
 */
export async function exportToExcel({
  template,
  data,
  fileName,
  columnFilter,
  isCurrent,
}: ExportOptions): Promise<void> {
  const XLSX = await import("xlsx");
  if (isCurrent && !isCurrent()) throw new DOMException("Report access changed before export completed.", "AbortError");

  const allBlocks = getAllBlocks(template);
  const tables = extractTables(allBlocks, data, template.name || "Report");

  if (tables.length === 0) {
    throw new Error("No exportable table data found in this report.");
  }

  deduplicateSheetNames(tables);

  const wb = XLSX.utils.book_new();

  for (const table of tables) {
    const cols = columnFilter
      ? table.columns.filter(columnFilter)
      : table.columns;

    if (cols.length === 0) continue;

    const headers = cols.map((c) => c.label);
    const rows = table.rows.map((row) =>
      cols.map((col) => {
        const raw = row[col.key];
        if (col.format === "date") {
          return parseDate(raw) ?? raw ?? "";
        }
        if (col.format === "money") {
          const n = Number(raw);
          return isNaN(n) ? 0 : Math.round(n * 100) / 100;
        }
        return raw ?? "";
      }),
    );

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // Apply column formatting (skip if worksheet is empty/mocked)
    if (ws && typeof ws === "object") {
      cols.forEach((col, i) => {
        const colLetter = XLSX.utils.encode_col(i);
        if (col.format === "date") {
          for (let r = 1; r <= rows.length; r++) {
            const cell = ws[`${colLetter}${r + 1}`];
            if (cell && cell.v instanceof Date) {
              cell.t = "d";
              cell.z = "mm/dd/yyyy";
            }
          }
        }
        if (col.format === "money") {
          for (let r = 1; r <= rows.length; r++) {
            const cell = ws[`${colLetter}${r + 1}`];
            if (cell && typeof cell.v === "number") {
              cell.z = '#,##0.00';
            }
          }
        }
      });

      // Freeze header row
      ws['!freeze'] = { xSplit: 0, ySplit: 1 };

      // AutoFilter on header row
      const lastCol = XLSX.utils.encode_col(cols.length - 1);
      ws['!autofilter'] = { ref: `A1:${lastCol}${rows.length + 1}` };

      // Bold header row (best-effort; community xlsx has limited style support)
      cols.forEach((_, ci) => {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: ci });
        if (ws[cellRef]) {
          ws[cellRef].s = { font: { bold: true } };
        }
      });

      // Auto-fit column widths based on header + data
      ws["!cols"] = cols.map((col, i) => {
        let maxLen = col.label.length;
        for (const row of rows) {
          const val = String(row[i] ?? "");
          maxLen = Math.max(maxLen, val.length);
        }
        return { wch: Math.min(maxLen + 2, 50) };
      });
    }

    XLSX.utils.book_append_sheet(wb, ws, table.sheetName);
  }

  const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([wbout], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  triggerDownload(blob, fileName);
}

/** Build a descriptive filename for a report export */
export function buildExportFileName(
  reportLabel: string,
  meta?: { clientName?: string; startDate?: string; endDate?: string },
): string {
  const parts = [reportLabel.replace(/\s+/g, "-").toLowerCase()];
  if (meta?.clientName) {
    parts.push(meta.clientName.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase());
  }
  if (meta?.startDate) parts.push(meta.startDate);
  if (meta?.endDate) parts.push(meta.endDate);
  return `${parts.join("-")}.xlsx`;
}
